import React from "react";
import {
  FaCheck,
  FaTimes,
  FaEdit,
} from "react-icons/fa";
import { IconButton, SoftButton, ToggleSwitch } from "../UI/DesignStandards";
import { splitBowlerName } from "../../utility/bowlerName";
import CatalogAverageNotice from "./CatalogAverageNotice";

const BowlerRow = React.memo(
  ({
    bowler,
    isEditing,
    editedAverage,
    editedHasAverage,
    errorMessage,
    canManage,
    verifyingBowlerId,
    verifyingAll,
    onEditClick,
    onAverageChange,
    onHasAverageChange,
    onSaveClick,
    onCancelClick,
    onVerifyClick,
    canVerify,
    canUnverify,
    onUseCatalogAverage,
    onReview,
  }) => {
    const handleVerifyToggle = () => {
      onVerifyClick(bowler.bowlerId, bowler.isVerified);
    };
    const verifyBusy =
      verifyingAll || verifyingBowlerId === bowler.bowlerId;
    const { lastName, firstName } = splitBowlerName(bowler.bowlerName);

    return (
      <tr className="border-b border-brand-charcoal/10 hover:bg-brand-cream/60 transition-colors">
        <td className="px-4 py-2 whitespace-nowrap">
          <div className="text-sm font-medium text-brand-charcoal">
            {firstName || "—"}
          </div>
        </td>
        <td className="px-4 py-2 whitespace-nowrap">
          <div className="text-sm text-brand-charcoal">
            {lastName || bowler.bowlerName || "Unknown"}
          </div>
        </td>

        <td className="px-4 py-2">
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 mr-3">
                <ToggleSwitch
                  checked={editedHasAverage}
                  onChange={(checked) => onHasAverageChange(checked)}
                  disabled={bowler.isVerified}
                  size="sm"
                />
                <span className="text-sm text-brand-muted">
                  {editedHasAverage ? "Has Average" : "No Average"}
                </span>
              </div>

              {editedHasAverage && (
                <input
                  type="number"
                  min="0"
                  max="300"
                  value={editedAverage}
                  onChange={onAverageChange}
                  className="w-20 px-2 py-1 border border-brand-charcoal/20 rounded-lg text-sm font-score bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                  placeholder="0-300"
                />
              )}

              <div className="flex space-x-1">
                <IconButton
                  icon={FaCheck}
                  variant="success"
                  size="sm"
                  onClick={() => onSaveClick(bowler.bowlerId)}
                  title="Save"
                />
                <IconButton
                  icon={FaTimes}
                  variant="danger"
                  size="sm"
                  onClick={onCancelClick}
                  title="Cancel"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-score font-medium text-brand-charcoal">
                {bowler.hasAverage === false ? "No Average" : bowler.average}
              </span>
              {canManage && !bowler.isVerified ? (
                <IconButton
                  icon={FaEdit}
                  variant="secondary"
                  size="sm"
                  onClick={() => onEditClick(bowler)}
                  title="Edit average"
                />
              ) : null}
            </div>
          )}
          {isEditing && errorMessage ? (
            <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
          ) : null}
          <CatalogAverageNotice
            bowler={bowler}
            canManage={canManage}
            verifying={verifyBusy}
            onUseCatalogAverage={onUseCatalogAverage}
          />
        </td>

        <td className="px-4 py-2 whitespace-nowrap">
          {bowler.isVerified ? (
            <span className="text-xs font-display font-bold text-brand-charcoal">
              Verified
            </span>
          ) : (
            <span className="text-xs font-display font-bold text-brand-muted">
              Not verified
            </span>
          )}
        </td>

        {canManage && (
          <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
            <div className="flex items-center justify-end space-x-2">
              {canVerify && onReview ? (
                <SoftButton
                  size="xs"
                  onClick={() => onReview(bowler.bowlerId)}
                  disabled={verifyBusy}
                  className="!rounded-lg"
                >
                  Review
                </SoftButton>
              ) : null}
              {canVerify && (
                <SoftButton
                  color="gold"
                  size="xs"
                  onClick={handleVerifyToggle}
                  disabled={verifyBusy}
                  iconLeft={
                    verifyingBowlerId === bowler.bowlerId ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                    ) : (
                      <FaCheck className="h-3 w-3" />
                    )
                  }
                  className="!rounded-lg"
                  title="Verify average"
                >
                  {verifyingBowlerId === bowler.bowlerId
                    ? "Verifying..."
                    : "Verify"}
                </SoftButton>
              )}
              {canUnverify && (
                <SoftButton
                  color="red"
                  size="xs"
                  onClick={handleVerifyToggle}
                  disabled={verifyBusy || bowler.hasVerifiedTeamScores}
                  iconLeft={
                    verifyingBowlerId === bowler.bowlerId ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                    ) : (
                      <FaTimes className="h-3 w-3" />
                    )
                  }
                  className={`!rounded-lg ${
                    bowler.hasVerifiedTeamScores
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  title={
                    bowler.hasVerifiedTeamScores
                      ? "Cannot unverify: Bowler is on teams with verified scores"
                      : "Unverify average"
                  }
                >
                  {verifyingBowlerId === bowler.bowlerId
                    ? "Unverifying..."
                    : "Unverify"}
                </SoftButton>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  }
);

BowlerRow.displayName = "BowlerRow";

export default BowlerRow;
